import React, { useState } from 'react';
import Store from '@store';
import { useSelector } from 'react-redux';
import { AiWrite } from '@desygner/ui-editor-ai-write';
import ReactAppBuilder from 'src/utils/react-app-builder';
import DesignFacade from 'assets/js/editor/facade/design/design';
import { constants } from '@config';

export const appContainer = 'ai-write-container';
class AiWriteApp extends ReactAppBuilder {
  constructor ({ appId, targetNodeSelector }) {
    super({ appId, targetNodeSelector });
  }

  mountApp () {
    super.mountApp(this.App);
  }

  closeApp () {
    super.closeApp(this.App);
  }

  handleBackButton = (setVisible, appNode) => {
    appNode && (appNode.style.width = '0');
    setVisible(false);

    setTimeout(() => this.closeApp(), 300);
  };

  handleTextInsertion = (text) => {
    const selectedElements = DesignFacade.getSelectedElements('text');
    const canChangeContent = INKIVE.RestrictionsFacade.canChange(selectedElements[0], 'change_content');
    const canAddText = INKIVE.RestrictionsFacade.isFunctionalityAllowed('text_add');

    if (selectedElements.length === 1 && canChangeContent) {
      this.#updateTextContent(selectedElements, text);
    } else if (canAddText) {
      this.#createTextContent(text);
    }
  };

  #updateTextContent = (selectedElements, text) => {
    const element = DesignFacade.get(selectedElements[0].id);
    const shouldAppendText = DesignFacade.canvas.getMode() === 'textedit';
    const content = shouldAppendText ? `${element.content}${text}` : text;

    shouldAppendText || DesignFacade.set({
      content
    });

    shouldAppendText && INKIVE.EditorFacade.canvas.runExtensions('insertContent', {
      content: text,
      elem: selectedElements[0]
    });
  };

  #createTextContent = (text) => {
    const state = Store.getState();
    const { canvasRelativeSize, zoom } = state.editor;

    const font = this.#getFont(canvasRelativeSize);
    const size = {
      width: this.#getWidth(text, font.size, canvasRelativeSize, zoom)
    };
    const position = {
      x: 20,
      y: 20
    };

    DesignFacade.create('text', {
      selected: true,
      content: text,
      size,
      position,
      font
    });
  };

  #getWidth = (text, fontSize, canvas, zoom) => {
    const halfCanvas = canvas.width / 2;
    const textLengthSize = text.length * 2 * fontSize * zoom;

    return (textLengthSize > halfCanvas) ? canvas.width - 40 : textLengthSize;
  };

  #getFont = (canvas) => {
    const baseFont = constants.tableOfContents.defaultHeadingStyle[0].size;
    const baseSize = Math.min(canvas.height, canvas.width);
    const fontSize = Math.round(baseSize * baseFont);

    return {
      size: fontSize,
      family: constants.tableOfContents.defaultHeadingStyle[0].fontFamily
    };
  };

  getTranslations = () => {
    return {
      cancelButton: INKIVE.TranslateHelper.translate('top_submenu.animation.confirm_split_cancel'),
      clearButton: INKIVE.TranslateHelper.translate('ai_write.app.clear_cta'),
      generateButton: INKIVE.TranslateHelper.translate('ai_write.app.generate_button'),
      generateButtonLoading: INKIVE.TranslateHelper.translate('ai_write.app.requestBtn_loading'),
      insertButton: INKIVE.TranslateHelper.translate('ai_write.app.insert_cta'),
      loadingMessage: INKIVE.TranslateHelper.translate('ai_write.app.loading_message'),
      loadingSlowMessage: INKIVE.TranslateHelper.translate('ai_write.app.loading_slow_message'),
      promptPlaceholder: INKIVE.TranslateHelper.translate('ai_write.app.placeholder'),
      regenerateButton: INKIVE.TranslateHelper.translate('ai_write.app.regenerate_button'),
      retrySampleButton: INKIVE.TranslateHelper.translate('ai_write.app.sample_retry'),
      sampleButton: INKIVE.TranslateHelper.translate('ai_write.app.sample_button'),
      subtitle: INKIVE.TranslateHelper.translate('ai_write.app.prompt'),
      title: INKIVE.TranslateHelper.translate('ai_write.title')
    };
  };

  getSamplePrompts = () => {
    return [
      'Generate a catchy headline for an article about sustainable living practices.',
      'Compose a persuasive email promoting a new line of eco-friendly clothing.',
      "Write an engaging Instagram post for a coffee shop's new seasonal menu.",
      'Write a compelling product description for a high-tech, waterproof smartwatch.',
      'Create an intriguing introduction for a blog post about the future of artificial intelligence.',
      'Summarize a recent news article about advancements in renewable energy.',
      'Craft a professional objective statement for a resume of a software engineer.'
    ];
  };

  App = () => {
    const [visible, setVisible] = useState(true);
    const appNode = document.getElementById(appContainer);
    const credentials = useSelector(state => state.user.userInfo.credentials);
    appNode && (appNode.style.width = '100%');

    const props = {
      visible,
      labels: this.getTranslations(),
      samplePrompts: this.getSamplePrompts(),
      apiBaseUrl: INKIVE.C.API_URL,
      company: CONFIG.company_id,
      userToken: credentials.api_key,
      insertNewAsset: this.handleTextInsertion.bind(this),
      handleBackButton: this.handleBackButton.bind(this, setVisible, appNode),
      loadingVideo: constants.loadingScreen.videoUrl
    };

    return <AiWrite {...props} />;
  };
}

export default new AiWriteApp({
  appId: appContainer,
  targetNodeSelector: '#tabText'
});
